import React, { useContext, useCallback, useEffect, useState } from "react";
import { connect } from "react-redux";

import { Dispatch, bindActionCreators } from "redux";

import { Dialog, CancelLink, SubmitButton } from "@plex/react-components";

import { commonMessages } from "infrastructure/localization";
import { INotification } from "infrastructure/notification";
import { Banner } from "ui/app";
import { DialogContainer, DialogNavigationContext } from "ui/app/navigation";
import { Upload } from "ui/components";
import { useLocalizedValues } from "ui/hooks";
import { glossaryMessages } from "ui/screens/plex-dictionary/plex-dictionary.localization";

import { termTranslationsUploadActions } from "./TermTranslationsUpload.actions";
import styles from "./TermTranslationsUpload.module.scss";
import { ITermTranslationsUploadState, ITermTranslationsUploadError } from "./TermTranslationsUpload.state";

export interface ITermTranslationsUploadProps {
  onSubmit: (file: File[]) => void;
  error?: ITermTranslationsUploadError;
  uploadProcessing: boolean;
  uploadCompleted: boolean;
  notifications: INotification[];
  resetState: () => void;
  onUploadCompleted: (error?: ITermTranslationsUploadError) => void;
  progress: number;
}

const TermTranslationsUpload: React.FC<ITermTranslationsUploadProps> = ({
  error,
  onSubmit,
  uploadProcessing,
  uploadCompleted,
  notifications,
  resetState,
  onUploadCompleted,
  progress
}) => {
  const [attachedFiles, setAttachedFiles] = useState<File[]>([]);

  const dialogNavigationContext = useContext(DialogNavigationContext);

  const localizedValues = useLocalizedValues(() => ({
    dialogTitle: glossaryMessages.action("selectTermTranslationsUploadFile"),
    submit: commonMessages.action("submit")
  }));

  const updateAttachedFilesList = useCallback(
    (files: File[]) => {
      setAttachedFiles(files);

      resetState();
    },
    [resetState]
  );

  const onHidden = useCallback(() => {
    if (!error) {
      resetState();
    }
  }, [error, resetState]);

  const onSubmitExecute = useCallback(() => {
    onSubmit(attachedFiles);
  }, [onSubmit, attachedFiles]);

  useEffect(() => {
    if (uploadCompleted) {
      dialogNavigationContext.hideDialog(undefined, onHidden);

      onUploadCompleted(error);
    }
  }, [uploadCompleted, onUploadCompleted, error, dialogNavigationContext, onHidden]);

  return (
    <DialogContainer
      onHidden={onHidden}
      render={dialogProps => {
        return (
          <Dialog {...dialogProps} title={localizedValues.dialogTitle}>
            <Dialog.Body>
              <Banner messages={notifications} messageClassName={styles.dialogBanner} />
              <Upload onFilesAttached={updateAttachedFilesList} uploadingProcessing={uploadProcessing} progress={progress} accept={[".xml"]} id={"termTranslationUploader"} />
            </Dialog.Body>
            <Dialog.Footer>
              <CancelLink onClick={dialogProps.onHide} />
              <SubmitButton type="button" onClick={onSubmitExecute}>
                {localizedValues.submit}
              </SubmitButton>
            </Dialog.Footer>
          </Dialog>
        );
      }}
    />
  );
};

export default connect(
  (state: { termTranslationsUpload: ITermTranslationsUploadState }) => {
    return {
      error: state.termTranslationsUpload.error,
      uploadProcessing: state.termTranslationsUpload.uploadProcessing,
      uploadCompleted: state.termTranslationsUpload.uploadCompleted,
      notifications: state.termTranslationsUpload.notifications,
      progress: state.termTranslationsUpload.progress
    };
  },
  (dispatch: Dispatch) => {
    return bindActionCreators(
      {
        onSubmit: termTranslationsUploadActions.uploadTranslations,
        resetState: termTranslationsUploadActions.resetUploadingTranslations
      },
      dispatch
    );
  }
)(TermTranslationsUpload);
